import React, { ChangeEvent, useState } from 'react';
import { observer } from 'mobx-react';
import validator from 'validator';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';

import { authMobileStyle, AuthModals } from '~/components/auth/AuthModal';
import authStore from '~/stores/authStore';

const langLib = {
    en: {
        password: 'Password *',
        passwordIsInvalid: 'Password is required (min 5 chars)',
        emailIsInvalid: 'Email is required',
        logIn: 'Log in',
        dontHaveAnAccount: `Don't have an account?`,
        createAnAccount: 'Create an account',
        forgotPass: 'Forgot Password?'
    },
    ru: {
        password: 'Пароль *',
        passwordIsInvalid: 'Пароль должен состоять минимум из 5 символов',
        emailIsInvalid: 'Email обязателен',
        logIn: 'Войти',
        dontHaveAnAccount: 'У вас нет аккаунта?',
        createAnAccount: 'Создать аккаунт',
        forgotPass: 'Забыли пароль?'
    }
} as const;

const LoginModal = ({ changeModalType }: { changeModalType: (type: AuthModals) => void }) => {
    const classes = authMobileStyle();
    const history = useHistory();

    const { lang } = authStore;
    const lib = langLib[lang];

    const [email, setEmail] = useState('');
    const handleEmail = (event: ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value.trim());
        setEmailIsInvalid(false);
    };
    const [isEmailInvalid, setEmailIsInvalid] = useState(false);
    const checkEmail = () => {
        const valid = validator.isEmail(email);
        setEmailIsInvalid(!valid);
        return valid;
    };

    const [password, setPassword] = useState('');
    const handlePassword = (event: ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value.trim());
        setPasswordIsInvalid(false);
        // if (!isConfirmInvalid) {
        //     checkConfirm();
        // }
    };
    const [isPasswordInvalid, setPasswordIsInvalid] = useState(false);
    const checkPassword = () => {
        const valid = password.length > 4;
        setPasswordIsInvalid(!valid);
        return valid;
    };

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSendRequest = async () => {
        if (checkEmail() && checkPassword()) {
            setIsLoading(true);
            setError('');
            try {
                await authStore.logIn(email, password);
                history.push('/admin');
            } catch (error) {
                setError(error?.message || error);
                setIsLoading(false);
            }
        }
    };

    return (
        <DialogContent dividers>
            {error && <Alert severity="error">{error}</Alert>}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        value={email}
                        onChange={handleEmail}
                        onBlur={checkEmail}
                        type="email"
                        error={isEmailInvalid}
                        helperText={isEmailInvalid ? lib['emailIsInvalid'] : ''}
                        label="Email *"
                        fullWidth
                        variant="standard"
                        autoComplete="off"
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        value={password}
                        onChange={handlePassword}
                        onBlur={checkPassword}
                        type="password"
                        label={lib['password']}
                        error={isPasswordInvalid}
                        helperText={isPasswordInvalid ? lib['passwordIsInvalid'] : ''}
                        fullWidth
                        variant="standard"
                        autoComplete="off"
                        disabled={isLoading}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DialogContentText className={classes.root}>
                        <Link
                            component="button"
                            color="secondary"
                            variant="body2"
                            onClick={() => changeModalType(AuthModals.forgotPass)}
                        >
                            {lib['forgotPass']}
                        </Link>
                    </DialogContentText>
                </Grid>
                <Grid item xs={12}>
                    <div className={classes.wrapper}>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={isLoading}
                            fullWidth
                            onClick={handleSendRequest}
                        >
                            {lib['logIn']}
                        </Button>
                        {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
                    </div>
                </Grid>
                <Grid item xs={12}>
                    <DialogContentText className={classes.root}>
                        <Typography variant="body2" component="span">
                            {lib['dontHaveAnAccount']}
                        </Typography>
                        <Link
                            component="button"
                            color="secondary"
                            variant="body2"
                            onClick={() => changeModalType(AuthModals.signUp)}
                        >
                            {lib['createAnAccount']}
                        </Link>
                    </DialogContentText>
                </Grid>
            </Grid>
        </DialogContent>
    );
};

export default observer(LoginModal);
